import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GrowProjectsResponse } from '@inyova/grow';
import { transformGrowAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { mergeMap, map, catchError, of, switchMap, filter, tap } from 'rxjs';
import * as AppRootActions from '@app/app.actions';
import * as fromGrow from './grow.reducers';
import * as fromRoot from '@app/app.reducers';

import { ToastNotificationService } from '@shared/services/toast-notification.service';

import * as GrowActions from './grow-actions';
import { GrowService } from './services/grow.service';

@Injectable()
export class GrowEffects {
  createGrowAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.createGrowAccount),
      mergeMap(() =>
        this.growService.createGrowAccount().pipe(
          map((response) => GrowActions.createGrowAccountSuccess({ data: transformGrowAccount(response) })),
          catchError(() => of(GrowActions.createGrowAccountFail()))
        )
      )
    )
  );

  createGrowAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.createGrowAccountSuccess),
      map(({ data }) => AppRootActions.fetchCustomerAccountDetail({ accountID: data.id }))
    )
  );

  getGrowRisk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.getGrowRisk),
      concatLatestFrom(() => this.store.select(fromGrow.selectGrowRiskLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(([{ language }]) =>
        this.growService.getGrowRisk().pipe(
          map(({ data, percentages }) => {
            return GrowActions.getGrowRiskSuccess({ data, percentages, language });
          }),
          catchError(() => of(GrowActions.getGrowRiskFail()))
        )
      )
    )
  );

  getGrowProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.getGrowProjects),
      concatLatestFrom(() => this.store.select(fromGrow.selectGrowProjectsLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.growService.getGrowProjects().pipe(
          map((response: GrowProjectsResponse) => {
            return GrowActions.getGrowProjectsSuccess(response);
          }),
          catchError(() => of(GrowActions.getGrowProjectsFail()))
        )
      )
    )
  );

  getGrowAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.getGrowAccount),
      concatLatestFrom(() => [this.store.select(fromRoot.selectGrowAccount)]),
      mergeMap(([, account]) => {
        if (!account) {
          return of(GrowActions.getGrowAccountFail());
        }
        return this.growService.getGrowAccount(account.id).pipe(
          map((response) => {
            return GrowActions.getGrowAccountSuccess({ data: transformGrowAccount(response) });
          }),
          catchError(() => of(GrowActions.getGrowAccountFail()))
        );
      })
    )
  );

  checkGrowRiskProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.checkGrowRiskProfile),
      switchMap(({ answers, availableAssets, initInvest }) =>
        this.growService.checkGrowRiskProfile(initInvest, availableAssets, answers).pipe(
          map((data) => GrowActions.checkGrowRiskProfileSuccess({ data })),
          catchError(() => of(GrowActions.checkGrowRiskProfileFail()))
        )
      )
    )
  );

  submitGrowRisk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.submitGrowRisk),
      concatLatestFrom(() => this.store.select(fromGrow.selectGrowOnboardingId)),
      switchMap(([{ data, id }, growOnboardingId]) =>
        this.growService.updateGrowRisk(data, id, growOnboardingId).pipe(
          mergeMap(() => [GrowActions.submitGrowRiskSuccess({ id })]),
          catchError(() => of(GrowActions.submitGrowRiskFail()))
        )
      )
    )
  );

  submitGrowRiskSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.submitGrowRiskSuccess),
      map(() => AppRootActions.fetchCustomer())
    )
  );

  submitGrowRiskFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GrowActions.submitGrowRiskFail),
        map(() => this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev') as string))
      ),
    { dispatch: false }
  );

  showToastGeneralGrowError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ...[GrowActions.submitGrowRiskFail, GrowActions.checkGrowRiskProfileFail, GrowActions.getGrowRiskFail, GrowActions.createGrowAccountFail]
        ),
        tap(() => {
          this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev') as string);
        })
      ),
    { dispatch: false }
  );

  markGrowProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.markGrowProject),
      switchMap(({ projectId }) =>
        this.growService.toggleGrowProjectSelection(projectId, true).pipe(map(() => GrowActions.markGrowProjectSuccess({ projectId })))
      )
    )
  );

  unmarkGrowProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrowActions.unmarkGrowProject),
      switchMap(({ projectId }) =>
        this.growService.toggleGrowProjectSelection(projectId, false).pipe(map(() => GrowActions.unmarkGrowProjectSuccess({ projectId })))
      )
    )
  );

  constructor(
    private actions$: Actions,
    private growService: GrowService,
    private router: Router,
    private store: Store,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService
  ) {}
}
